import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

const ENTITY_PLURAL_MAP = {
  contest: 'contests',
  round: 'rounds',
  pool: 'pools',
  user: 'users',
  super_admin: 'super_admins',
  admin: 'admins',
  organizer: 'organizers',
  judge: 'judges',
  dancer: 'dancers',
  score: 'scores',
  participant: 'participants',
} as const;

export type SingularEntityKey = keyof typeof ENTITY_PLURAL_MAP;
export type PluralEntityKey = (typeof ENTITY_PLURAL_MAP)[SingularEntityKey];
export type EntityKey = SingularEntityKey | PluralEntityKey;

export interface TranslationDescriptor {
  __translation: true;
  key: string;
  params?: Record<string, unknown>;
  fallback?: string;
  lower?: boolean;
}

const ENTITY_SINGULAR_MAP: Record<PluralEntityKey, SingularEntityKey> = Object.fromEntries(
  Object.entries(ENTITY_PLURAL_MAP).map(([singular, plural]) => [plural, singular]),
) as Record<PluralEntityKey, SingularEntityKey>;

const pluralKeys = new Set<PluralEntityKey>(Object.values(ENTITY_PLURAL_MAP));

const toSingular = (key: EntityKey): SingularEntityKey =>
  pluralKeys.has(key as PluralEntityKey)
    ? ENTITY_SINGULAR_MAP[key as PluralEntityKey]
    : (key as SingularEntityKey);

const toPlural = (key: SingularEntityKey): PluralEntityKey =>
  ENTITY_PLURAL_MAP[key] ?? (`${key}s` as PluralEntityKey);

@Injectable({ providedIn: 'root' })
export class I18nUtil {
  private transloco: TranslocoService = inject(TranslocoService);

  // Returns translated noun with desired form and casing
  noun(
    key: EntityKey,
    options?: { lower?: boolean; form?: 'singular' | 'plural' | 'partitive' },
  ): string {
    const lower = options?.lower ?? false;
    const normalizedKey = String(key) as EntityKey;
    const base = toSingular(normalizedKey);
    const form = this._resolveForm(normalizedKey, options?.form);

    // Prefer nested forms under nounForms
    const nestedPath = `nounForms.${base}.${form}`;
    let val = this.transloco.translate(nestedPath);

    // Fallback if key is missing from nounForms
    if (val === nestedPath) {
      // For partitive, fallback to plural if missing
      if (form === 'partitive') {
        const pluralPath = `nounForms.${base}.plural`;
        val = this.transloco.translate(pluralPath);
        if (val === pluralPath) {
          val = toPlural(base); // Final fallback
        }
      } else {
        val = form === 'singular' ? base : toPlural(base);
      }
    }

    return lower ? val.toLowerCase() : val;
  }

  nounKey(
    key: EntityKey,
    options?: {
      form?: 'singular' | 'plural' | 'partitive';
      lower?: boolean;
    },
  ): TranslationDescriptor {
    const normalizedKey = String(key) as EntityKey;
    const base = toSingular(normalizedKey);
    const resolvedForm = this._resolveForm(normalizedKey, options?.form);

    return {
      __translation: true,
      key: `nounForms.${base}.${resolvedForm}`,
      lower: options?.lower ?? false,
      fallback: this.noun(normalizedKey, {
        form: resolvedForm,
        lower: options?.lower,
      }),
    };
  }

  // Build pluralization params for ICU messages
  // entity can be singular or plural; we derive both keys
  getPluralParams(
    entity: EntityKey,
    count: number,
    options?: { lower?: boolean },
  ): {
    count: number;
    itemSingular: TranslationDescriptor;
    itemPlural: TranslationDescriptor;
  } {
    const base = toSingular(entity);
    const pluralKey = toPlural(base);
    const itemSingular = this.nounKey(base, {
      form: 'singular',
      lower: options?.lower,
    });
    const itemPlural = this.nounKey(pluralKey, {
      form: 'plural',
      lower: options?.lower,
    });
    return { count, itemSingular, itemPlural };
  }

  // Maps roles to i18n keys (supports snake_case)
  roleKey(role: string): string {
    return `systemRole.${role.toLowerCase()}`;
  }

  roleDescriptionKey(role: string): string {
    return `systemRoleDescription.${role.toLowerCase()}`;
  }

  private _resolveForm(
    key: EntityKey,
    form?: 'singular' | 'plural' | 'partitive',
  ): 'singular' | 'plural' | 'partitive' {
    return form ?? (pluralKeys.has(key as PluralEntityKey) ? 'plural' : 'singular');
  }
}
